import { useState, useEffect } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { PanelLeft } from 'lucide-react';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { ProfileDropdown } from './ui/ProfileDropdown';
import { useAuth } from '../hooks/useAuth';
import { PageErrorBoundary } from './errors';
import { BannerManager } from './BannerManager';
import { useBannerPositioning } from '../hooks/useBannerPositioning';

interface LayoutProps {
  children?: React.ReactNode;
}

function LayoutContent({ children }: LayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { logout, user } = useAuth();
  const { dropdownPosition, logoPosition, contentPosition } = useBannerPositioning();

  // Trigger window resize event when sidebar state changes
  useEffect(() => {
    // Small delay to ensure the DOM has updated
    const timeoutId = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 300); // Match this with the sidebar transition duration

    return () => clearTimeout(timeoutId);
  }, [isSidebarCollapsed]);

  return (
    <div className="h-screen flex overflow-hidden bg-theme-light-page-bg dark:bg-theme-dark-page-bg">
      {/* Mobile profile dropdown - positioned to not conflict with fixed logo */}
      <div className={`lg:hidden fixed ${dropdownPosition} right-4 z-50`}>
        {user && <ProfileDropdown user={user} onLogout={logout} />}
      </div>

      <Sidebar
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        isSidebarCollapsed={isSidebarCollapsed}
      />

      {/* Fixed logo and toggle button - always visible */}
      <div className={`flex fixed ${logoPosition} left-6 z-50 items-center`}>
        <Link to="/overview">
          <img
            src="/assets/jua-logo.svg"
            alt="Logo"
            className="h-7 dark:invert dark:brightness-200"
          />
        </Link>
        <button
          onClick={() => {
            // On mobile, toggle mobile menu. On desktop, toggle sidebar collapsed state
            if (window.innerWidth < 1024) {
              setIsMobileMenuOpen(!isMobileMenuOpen);
            } else {
              setIsSidebarCollapsed(!isSidebarCollapsed);
            }
          }}
          className="ml-6 p-2 bg-theme-light-page-bg dark:bg-theme-dark-page-bg lg:bg-transparent text-theme-light-text-secondary dark:text-theme-dark-text-secondary rounded-md mt-[2px] shadow-sm lg:shadow-none"
          data-ph-capture-attribute="btn-sidebar-toggle"
        >
          <PanelLeft className="h-4 w-4" />
        </button>
      </div>

      <div
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ease-in-out ${contentPosition} ${!isSidebarCollapsed ? 'lg:ml-[200px]' : 'lg:ml-1'}`}
      >
        <Navbar />
        {/* Main content wrapper with card design */}
        <div className="flex-1 overflow-hidden p-0 lg:p-2 lg:pb-3 lg:pr-3">
          <main className="h-full overflow-auto mobile-main-content bg-theme-light-bg dark:bg-theme-dark-bg lg:rounded-xl lg:border lg:border-gray-200 dark:lg:border-gray-700">
            <PageErrorBoundary>{children || <Outlet />}</PageErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  );
}

// Export the Layout wrapped with BannerManager
export default function Layout({ children }: LayoutProps = {}) {
  return (
    <BannerManager>
      <LayoutContent>{children}</LayoutContent>
    </BannerManager>
  );
}
